import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { MarketData, Species, User } from 'common/src/utils/types';
import { getAllMarketData } from 'common/src/api/market_data';
import { SEASONS } from 'common/src/utils/definitions';

export type PriceLevel = 'market' | 'fob' | 'producer';

type props = {
  allMarketData: MarketData[];
  setAllMarketData: (allMarketData: MarketData[]) => void;
  fetchAllMarketData: () => void;
  selectedSeasonsMarketData: string[];
  setSelectedSeasonsMarketData: (selectedSeasons: string[]) => void;
  loadingMarketData: boolean;
  setLoadingMarketData: (loadingMarketData: boolean) => void;
  selectedSpecies: Species | null;
  setSelectedSpecies: (selectedSpecies: Species) => void;
  species: Species[];
  setSpecies: (species: Species[]) => void;
  priceLevel: PriceLevel;
  setPriceLevel: (priceLevel: PriceLevel) => void;
};

// Helper function to get current season and previous season
const getDefaultSeasons = (): string[] => {
  const seasonNames = Object.values(SEASONS).map(s => s.name);
  const currentSeason = seasonNames[seasonNames.length - 1]; // Last season is current

  // Find index of current season and get previous one
  const currentIndex = seasonNames.indexOf(currentSeason);
  const previousSeason = currentIndex > 0 ? seasonNames[currentIndex - 1] : null;

  // Return current + previous (if exists)
  return previousSeason ? [previousSeason, currentSeason] : [currentSeason];
};

const useDataStore = create<props>()(
  persist(
    (set) => ({
      allMarketData: [],
      setAllMarketData: (allMarketData: MarketData[]) => set({ allMarketData }),
      clearAllMarketData: () => set({ allMarketData: [] }),
      fetchAllMarketData: async () => {
        const { selectedSeasonsMarketData, selectedSpecies } = useDataStore.getState();
        set({ loadingMarketData: true });
        const allMarketData = await getAllMarketData(
          selectedSeasonsMarketData.length > 0
            ? selectedSeasonsMarketData
            : undefined,
          selectedSpecies?.id
        );
        const validMarketData = allMarketData.filter(
          (item) => item.price_kg !== null && item.price_kg > 0
        );
        set({ allMarketData: validMarketData });
        set({ loadingMarketData: false });
      },
      selectedSeasonsMarketData: getDefaultSeasons(),
      setSelectedSeasonsMarketData: async (selectedSeasons: string[]) => {
        set({ selectedSeasonsMarketData: selectedSeasons });
        useDataStore.getState().fetchAllMarketData();
      },
      loadingMarketData: false,
      setLoadingMarketData: (loadingMarketData: boolean) =>
        set({ loadingMarketData }),
      selectedSpecies: null,
      setSelectedSpecies: (selectedSpecies: Species) => {
        set({ selectedSpecies, priceLevel: 'market' });
        // Reload market data when species changes
        useDataStore.getState().fetchAllMarketData();
      },
      species: [],
      setSpecies: (species: Species[]) => set({ species }),
      priceLevel: 'market',
      setPriceLevel: (priceLevel: PriceLevel) => set({ priceLevel }),
    }),
    {
      name: 'cherry-market-storage',
      partialize: (state) => ({
        selectedSpecies: state.selectedSpecies,
        priceLevel: state.priceLevel,
      }),
    }
  )
);

export default useDataStore;
